"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import type { BlogPostSummary } from "@/modules/blog/types";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

export default function BlogListClient(props: Readonly<{ posts: ReadonlyArray<BlogPostSummary> }>) {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();
  const query = sp.get("q") ?? "";
  const activeTag = sp.get("tag") ?? "";
  const sort = (sp.get("sort") as "newest" | "featured") === "featured" ? "featured" : "newest";

  const [queryInput, setQueryInput] = useState<string>(query);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(sp.toString());
    if (!value) next.delete(key);
    else next.set(key, value);
    const url = pathname + (next.toString() ? "?" + next.toString() : "");
    const current = pathname + (sp.toString() ? "?" + sp.toString() : "");
    if (url === current) return;
    router.replace(url, { scroll: false });
  }
  function setQuery(v: string): void {
    setQueryInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(function run() {
      setParam("q", v);
    }, 200);
  }
  function setActiveTag(v: string): void {
    setParam("tag", v);
  }
  function setSort(v: "newest" | "featured"): void {
    setParam("sort", v);
  }

  const tags = useMemo(
    function getTags() {
      return Array.from(
        new Set(
          props.posts.flatMap(function f(p) {
            return p.tags;
          })
        )
      ).sort();
    },
    [props.posts]
  );

  const filtered = useMemo(
    function filter() {
      const q = queryInput.trim().toLowerCase();
      const base = props.posts.filter(function match(p) {
        const matchesQuery =
          !q ||
          p.title.toLowerCase().includes(q) ||
          (p.excerpt ?? "").toLowerCase().includes(q) ||
          p.tags.some(function t(tg) {
            return tg.toLowerCase().includes(q);
          });
        const matchesTag = !activeTag || p.tags.includes(activeTag as any);
        return matchesQuery && matchesTag;
      });
      return base.sort(function s(a, b) {
        if (sort === "featured") {
          const fa = a.featured ? 1 : 0;
          const fb = b.featured ? 1 : 0;
          if (fa !== fb) return fb - fa;
        }
        const da = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const db = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        return db - da;
      });
    },
    [props.posts, queryInput, activeTag, sort]
  );

  useEffect(
    function syncFromUrl() {
      setQueryInput(query);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [query]
  );

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="w-full sm:w-80">
          <label htmlFor="blog-search" className="sr-only">
            Search posts
          </label>
          <input
            id="blog-search"
            value={queryInput}
            onChange={function onChange(e) {
              setQuery(e.target.value);
            }}
            placeholder="Search posts..."
            aria-label="Search posts"
            className="w-full rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={function clickAll() {
              setActiveTag("");
            }}
            className={
              "rounded-full border px-3 py-1 text-xs " +
              (!activeTag
                ? "border-blue text-blue"
                : "border-white/15 text-foreground hover:bg-white/10")
            }
            aria-pressed={!activeTag}
            data-action="filter-all"
          >
            All
          </button>
          {tags.map(function renderTag(t) {
            const isActive = activeTag === t;
            return (
              <button
                key={t}
                type="button"
                onClick={function clickTag() {
                  setActiveTag(isActive ? "" : t);
                }}
                className={
                  "rounded-full border px-3 py-1 text-xs " +
                  (isActive
                    ? "border-blue text-blue"
                    : "border-white/15 text-foreground hover:bg-white/10")
                }
                aria-pressed={isActive}
                aria-label={`Filter by tag ${t}`}
                data-action="filter-tag"
              >
                #{t}
              </button>
            );
          })}
          <div className="ml-2 hidden sm:block h-4 w-px bg-white/15" aria-hidden="true" />
          <div className="inline-flex items-center gap-1 [&>button]:cursor-pointer">
            <span className="text-xs text-muted-foreground mr-2">Sort</span>
            <button
              type="button"
              className={
                "rounded-full px-2 py-0.5 text-xs " +
                (sort === "newest" ? "bg-blue text-white" : "text-foreground/80 hover:bg-white/10")
              }
              onClick={function s() {
                setSort("newest");
              }}
              aria-pressed={sort === "newest"}
              data-action="sort-newest"
            >
              Newest
            </button>
            <button
              type="button"
              className={
                "rounded-full px-2 py-0.5 text-xs " +
                (sort === "featured"
                  ? "bg-blue text-white"
                  : "text-foreground/80 hover:bg-white/10")
              }
              onClick={function s() {
                setSort("featured");
              }}
              aria-pressed={sort === "featured"}
              data-action="sort-featured"
            >
              Featured
            </button>
          </div>
        </div>
      </div>

      <ul className="grid gap-4">
        <li className="text-xs text-muted-foreground">{filtered.length} results</li>
        {filtered.map(function renderPost(p) {
          return (
            <li
              key={p.id}
              className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur dark:border-white/10 dark:bg-white/5"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <Link
                    href={`/blog/${p.slug}`}
                    className="text-base font-semibold text-foreground"
                    prefetch
                  >
                    {p.title}
                  </Link>
                  {p.excerpt && <p className="mt-1 text-sm text-muted-foreground">{p.excerpt}</p>}
                  {p.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.tags.map(function renderTag(t) {
                        return (
                          <span
                            key={t}
                            className="rounded-md border border-white/15 px-2 py-0.5 text-xs text-foreground/80"
                          >
                            {t}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.publishedAt && new Date(p.publishedAt).toLocaleDateString()}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
